import React from "react";
import { useTaxSelector } from "../redux/general/reduxStore";

/**
 * Chatbot Maica – záložka na pravém okraji obrazovky, kterou si vykresluje cizí skript
 * z `portal.multima.ai`.
 *
 * Zapíná ho **backend**, ne frontend: property `info.app.chatbot` v `application-<profil>.properties`,
 * v nasazení přebitelná proměnnou prostředí `INFO_APP_CHATBOT`. Hodnota přijde v `/actuator/info`
 * vedle profilu, takže se čte úplně stejnou cestou jako odznak prostředí v hlavičce.
 *
 * Produkce má property natvrdo `false` (zadání PM z 2026-09-14) a odsud ji zapnout nejde –
 * musel by to říct backend. Proto tu není žádná podmínka na jméno prostředí: kdyby chatbot měl
 * jednou běžet i na produkci, nemění se kód, jen konfigurace.
 */

/** Doména, ze které si chatbot tahá všechno své. Podle ní se pozná, co po něm uklidit. */
const MAICA_HOST = "portal.multima.ai";

/**
 * Adresa skriptu podle zadání od PM. Proti jeho textu je opravený konec: `iconFile=button.png%22`
 * obsahovalo URL-zakódovanou uvozovku, která se tam dostala při kopírování z HTML – `%22` je `"`,
 * takže by se atribut `src` neuzavřel tam, kde má.
 */
const MAICA_SCRIPT_SRC =
    `https://${MAICA_HOST}/js/maica-bot-generator.js` +
    "?customerId=10e13479-c55c-4d0c-9544-1d54683017c0" +
    `&botURL=https://${MAICA_HOST}` +
    "&buttonBackgroundColor=3a37f6" +
    "&iconFile=button.png";

/** Podle ní se pozná už vložený skript, aby se při přemountování nevkládal podruhé. */
const SCRIPT_ID = "maica-bot-generator";

/** Začíná na `maica`, takže ho `removeChatbot` uklidí spolu s widgetem. */
const TAB_STYLE_ID = "maica-tab-style";

/**
 * Tlačítko widgetu jako záložka přilepená na pravý okraj stránky místo pulzujícího kruhu.
 *
 * Tlačítko i jeho styl (`maica-bot.css`) jsou z cizí domény a jejich CSS se načte až po tomhle,
 * proto selektor `button#maica_button` – má vyšší specificitu než `#maica_button` i
 * `.pulsating-button:hover` widgetu a vyhraje i bez `!important`. Třídu `pulsating-button`
 * si skript při otevření a zavření okna sám přidává a odebírá, proto se animace vypíná tady.
 * Barvu pozadí nastavuje skript inline z `buttonBackgroundColor`, ta zůstává.
 * `bottom` je stejný jako u původního kruhu, aby okno chatu (`bottom: 130px`) sedělo nad záložkou.
 */
const TAB_STYLE = `
button#maica_button {
    right: 0;
    bottom: 38px;
    width: 44px;
    height: 68px;
    border-radius: 12px 0 0 12px;
    box-shadow: -2px 2px 6px rgba(0, 0, 0, 0.25);
    animation: none;
    transform: none;
    transition: width 0.2s ease;
}
button#maica_button:hover {
    width: 52px;
    transform: none;
}
button#maica_button:active {
    transform: none;
}
button#maica_button:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -4px;
}
button#maica_button #maica_icon {
    width: 28px;
    height: 28px;
    padding-left: 6px;
}
`;

/**
 * Úklid po chatbotovi. Odhlášení v téhle aplikaci **nenačítá stránku znovu** – jen se přestane
 * vykreslovat obsah a místo něj přijde přihlašovací formulář. Bez tohoto úklidu by widget
 * zůstal visící nad přihlašováním, cizí skript by běžel ve chvíli, kdy se píše heslo, a dalšímu
 * uživateli v téže záložce by se otevřela konverzace toho předchozího.
 *
 * Generátor si kromě vlastních prvků (`maica_button`, `maica_iframe`, `maica_close`) dotažuje
 * ještě další skript a styl z téže domény – ověřeno v prohlížeči, proto se mazá i podle ní.
 */
const removeChatbot = () => {
    const selector = [`[id^="maica"]`, `script[src*="${MAICA_HOST}"]`, `link[href*="${MAICA_HOST}"]`].join(", ");
    document.querySelectorAll(selector).forEach((element) => element.remove());
};

/**
 * Actuator vrací hodnoty z `info.*` jako řetězce, ne jako JSON boolean – a řetězec `"false"` je
 * v JavaScriptu pravdivý, takže prosté `if (chatbot)` by chatbota zapnulo i tam, kde je vypnutý.
 * Proto se porovnává na text a všechno ostatní (chybějící hodnota, překlep v konfiguraci) znamená
 * vypnuto: u cizího skriptu na produkci je tichý nesoulad horší než chatbot, který se neukáže.
 */
const isEnabled = (value: unknown): boolean => String(value).trim().toLowerCase() === "true";

const MaicaChatbot = () => {
    const { actuatorInfo } = useTaxSelector((state) => state.taxApp);
    const enabled = isEnabled(actuatorInfo?.app?.chatbot);

    React.useEffect(() => {
        if (!enabled || document.getElementById(SCRIPT_ID)) {
            return;
        }
        const style = document.createElement("style");
        style.id = TAB_STYLE_ID;
        style.textContent = TAB_STYLE;
        document.head.appendChild(style);

        const script = document.createElement("script");
        script.id = SCRIPT_ID;
        script.src = MAICA_SCRIPT_SRC;
        // `defer` ze zadání nemá u skriptu vloženého skriptem význam – ten je asynchronní vždy.
        script.async = true;
        script.setAttribute("data-maica", "0");
        document.head.appendChild(script);

        return removeChatbot;
    }, [enabled]);

    return null;
};

export default MaicaChatbot;
